import {
  readExplicitAdvertisingConsent,
  toIntakeConsent,
  type AdvertisingConsentSnapshot,
} from './consent/advertising-consent'

/**
 * Rep / UTM / click-ID attribution captured at a landing page (e.g.
 * /book?rep=42, /?gclid=…) and persisted in localStorage so a returning
 * visitor still gets credited for the booking they complete days or weeks
 * later.
 *
 * Save semantics (issues #79/#80):
 * - The campaign bag (repCode, utm*, clickIds) is last-write-wins as a unit —
 *   but only a save that carries campaign fields replaces it. A plain
 *   session-start save (referrer/landingPath only) leaves the stored bag and
 *   its capturedAt untouched, so an ad click keeps its credit and its
 *   original 30-day window when the visitor returns organically.
 * - referrer/landingPath are first-touch: once a landing has been recorded,
 *   later saves never overwrite them (until the record expires).
 * Reads enforce the ATTRIBUTION_TTL_DAYS TTL (stale entries are cleared on
 * read so the call site never has to think about it).
 */

/**
 * Cap on the URL-shaped fields (referrer, landingPath), shared with the
 * intake schema. The capture truncates to it so a pathological query string
 * keeps its useful prefix; the schema drops anything still longer (tampered
 * payloads) rather than rejecting the booking.
 */
export const ATTRIBUTION_URL_FIELD_MAX = 2048

/** Ad-platform click-ID params captured from landing URLs (issue #79). */
export const CLICK_ID_PARAMS = ['gclid', 'fbclid', 'msclkid', 'wbraid', 'gbraid', 'oppref'] as const
export type ClickIdParam = (typeof CLICK_ID_PARAMS)[number]
export type ClickIds = Partial<Record<ClickIdParam, string>>

/** URL and cookie evidence carried with a campaign touch. IDs stay strings. */
export type MetaAttributionEvidence = {
  campaignId?: string
  adSetId?: string
  adId?: string
  placement?: string
  publisher?: string
  fbc?: string
  fbp?: string
}

const META_ID_PATTERN = /^\d{6,30}$/
const META_FBC_PATTERN = /^fb\.\d+\.\d{13}\.[^.\s]+$/
const META_FBP_PATTERN = /^fb\.\d+\.\d{13}\.\d+$/
const META_TEXT_MAX = 100

export const META_URL_PARAMS = {
  campaignId: 'meta_campaign_id',
  adSetId: 'meta_adset_id',
  adId: 'meta_ad_id',
  placement: 'meta_placement',
  publisher: 'meta_publisher',
} as const

export function isValidMetaId(value: string | undefined): value is string {
  return !!value && META_ID_PATTERN.test(value) && !value.includes('{{')
}

/** `_fbc`: documented fb.${subdomain_index}.${creation_time}.${fbclid}. */
export function isValidMetaFbc(value: string | undefined): value is string {
  return !!value && value.length <= 512 && META_FBC_PATTERN.test(value)
}

/** `_fbp`: documented fb.${subdomain_index}.${creation_time}.${random_number}. */
export function isValidMetaFbp(value: string | undefined): value is string {
  return !!value && value.length <= 512 && META_FBP_PATTERN.test(value)
}

export type Attribution = {
  repCode?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmContent?: string
  utmTerm?: string
  clickIds?: ClickIds
  /** Optional Meta campaign context; it does not by itself prove paid acquisition. */
  meta?: MetaAttributionEvidence
  /** First-touch external referrer (absent on direct visits and same-origin navigations). */
  referrer?: string
  /** First-touch pathname + search of the first page seen this attribution window. */
  landingPath?: string
  /** Versioned advertising-measurement choice and its express/implied basis. */
  advertisingConsent?: AdvertisingConsentSnapshot
  capturedAt: string // ISO 8601
}

/**
 * How long a rep/UTM capture stays credit-worthy, everywhere it is persisted
 * (localStorage, cg_rep cookie, Redis IP fallback) and re-checked server-side
 * in resolveLeadSource. Matches the dashboard's 30-day attribution window
 * (LEAD_SOURCE_MAX_CAPTURE_AGE_DAYS) — issue #71: a capture older than this
 * must never reach the dashboard as a rep-attributed lead.
 */
export const ATTRIBUTION_TTL_DAYS = 30
export const ATTRIBUTION_TTL_MS = ATTRIBUTION_TTL_DAYS * 24 * 60 * 60 * 1000

/**
 * Channel implied by a click ID when no UTM tagging is present (issue #79):
 * gclid/wbraid/gbraid only ever come from Google Ads auto-tagging (paid) and
 * msclkid from Microsoft Ads (paid); fbclid is appended to ALL outbound Meta
 * clicks, so it names the source but cannot assert a paid medium.
 */
export function clickIdDerivedSource(
  clickIds: ClickIds | undefined,
): { source: string; medium?: string } | null {
  if (!clickIds) return null
  if (clickIds.gclid || clickIds.wbraid || clickIds.gbraid) return { source: 'google', medium: 'cpc' }
  if (clickIds.msclkid) return { source: 'bing', medium: 'cpc' }
  if (clickIds.oppref) return { source: 'chatgpt', medium: 'paid' }
  if (clickIds.fbclid) return { source: 'facebook' }
  return null
}

/**
 * Stable identity of the campaign fields alone — the dedupe key that stops
 * SPA re-renders from re-firing capture. Null when there is no campaign
 * signal (a plain landing dedupes by the isLanding flag instead).
 */
export function campaignFingerprint(a: Attribution): string | null {
  if (!hasCampaignFields(a)) return null
  return [
    a.repCode,
    a.utmSource,
    a.utmMedium,
    a.utmCampaign,
    a.utmContent,
    a.utmTerm,
    ...CLICK_ID_PARAMS.map((p) => a.clickIds?.[p]),
    a.meta?.campaignId,
    a.meta?.adSetId,
    a.meta?.adId,
    a.meta?.placement,
    a.meta?.publisher,
  ]
    .map((v) => v ?? '')
    .join('|')
}

export type AttributionCaptureContext = {
  /** document.referrer at this page view ('' when direct). */
  referrer: string
  /** window.location.origin — a same-origin referrer is internal navigation, not a source. */
  origin: string
  /** window.location.pathname + window.location.search of this page view. */
  path: string
  /** True only on the first page view after a full page load. */
  isLanding: boolean
  /** Validated explicit choice read from the first-party consent cookie. */
  advertisingConsent?: AdvertisingConsentSnapshot
}

/**
 * Pure builder for the attribution capture on a page view. Returns null when
 * there is nothing to store (an SPA navigation with no campaign params).
 * Only the landing view contributes referrer/landingPath (first-touch,
 * issue #80) — and a landing always produces a capture, even from a bare
 * URL, so untagged visits stop being an attribution black hole.
 */
export function buildAttributionCapture(
  params: { get(name: string): string | null },
  ctx: AttributionCaptureContext,
): Attribution | null {
  const capture: Attribution = { capturedAt: new Date().toISOString() }
  const advertisingConsent = toIntakeConsent(ctx.advertisingConsent)
  if (advertisingConsent) capture.advertisingConsent = advertisingConsent

  const repCode = params.get('rep')
  const utmSource = params.get('utm_source')
  const utmMedium = params.get('utm_medium')
  const utmCampaign = params.get('utm_campaign')
  const utmContent = params.get('utm_content')
  const utmTerm = params.get('utm_term')
  if (repCode) capture.repCode = repCode
  if (utmSource) capture.utmSource = utmSource
  if (utmMedium) capture.utmMedium = utmMedium
  if (utmCampaign) capture.utmCampaign = utmCampaign
  if (utmContent) capture.utmContent = utmContent
  if (utmTerm) capture.utmTerm = utmTerm

  const clickIds: ClickIds = {}
  for (const param of CLICK_ID_PARAMS) {
    const value = params.get(param)
    if (value) clickIds[param] = value
  }
  if (Object.keys(clickIds).length > 0) capture.clickIds = clickIds

  const meta: MetaAttributionEvidence = {}
  for (const [key, param] of Object.entries(META_URL_PARAMS) as Array<
    [keyof typeof META_URL_PARAMS, (typeof META_URL_PARAMS)[keyof typeof META_URL_PARAMS]]
  >) {
    const value = params.get(param) ?? undefined
    if (key === 'campaignId' || key === 'adSetId' || key === 'adId') {
      if (isValidMetaId(value)) meta[key] = value
    } else if (value && value.length <= META_TEXT_MAX && !value.includes('{{')) {
      meta[key] = value
    }
  }
  if (Object.keys(meta).length > 0) capture.meta = meta

  if (!ctx.isLanding) {
    return hasCampaignFields(capture) ? capture : null
  }

  capture.landingPath = ctx.path.slice(0, ATTRIBUTION_URL_FIELD_MAX)
  if (ctx.referrer) {
    try {
      if (new URL(ctx.referrer).origin !== ctx.origin) {
        capture.referrer = ctx.referrer.slice(0, ATTRIBUTION_URL_FIELD_MAX)
      }
    } catch {
      // Malformed referrer — treat as absent.
    }
  }
  return capture
}

const STORAGE_KEY = 'cg_attribution'
const COOKIE_NAME = 'cg_rep'
const COOKIE_MAX_AGE = ATTRIBUTION_TTL_DAYS * 24 * 60 * 60 // seconds
const TTL_MS = ATTRIBUTION_TTL_MS

function getStorage(): Storage | null {
  try {
    return (globalThis as { localStorage?: Storage }).localStorage ?? null
  } catch {
    return null
  }
}

function getDoc(): { cookie: string } | null {
  try {
    return (globalThis as { document?: { cookie: string } }).document ?? null
  } catch {
    return null
  }
}

/** True when the capture carries a campaign signal (rep, UTM, or click ID). */
export function hasCampaignFields(a: Attribution): boolean {
  return Boolean(
    a.repCode ||
      a.utmSource ||
      a.utmMedium ||
      a.utmCampaign ||
      a.utmContent ||
      a.utmTerm ||
      (a.clickIds && Object.keys(a.clickIds).length > 0) ||
      Boolean(a.meta?.campaignId || a.meta?.adSetId || a.meta?.adId),
  )
}

export function saveAttribution(next: Attribution): void {
  const storage = getStorage()
  if (!storage) return
  // Merge against the stored record only — never the cg_rep cookie fallback,
  // which carries no timestamp and would mint a fresh capturedAt (the server
  // re-merges the cookie rep code in resolveLeadSource instead).
  const existing = readStoredAttribution(storage)
  let merged: Attribution
  if (!existing) {
    merged = next
  } else {
    // Campaign bag + capturedAt travel together: a campaign save replaces
    // both; a plain save keeps both (the credit window must not be extended
    // by later organic visits — issue #71's TTL alignment).
    merged = hasCampaignFields(next) ? { ...next } : { ...existing }
    if (existing.landingPath !== undefined) {
      // First touch already recorded (possibly a direct visit with no
      // referrer) — freeze both fields.
      merged.referrer = existing.referrer
      merged.landingPath = existing.landingPath
    } else {
      merged.referrer = next.referrer
      merged.landingPath = next.landingPath
    }
  }
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(merged))
  } catch {
    // Quota / private-mode failures are silent — attribution is best-effort.
  }
  // Cookie refresh is keyed to the incoming capture, not the merged record —
  // only a real ?rep= visit may extend the cookie's 30-day window.
  if (next.repCode) {
    try {
      const doc = getDoc()
      if (doc) {
        doc.cookie = `${COOKIE_NAME}=${next.repCode}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax; Secure`
      }
    } catch {
      // Silent — cookie write is best-effort.
    }
  }
}

function readCookieRepCode(): string | null {
  try {
    const doc = getDoc()
    if (!doc) return null
    const prefix = `${COOKIE_NAME}=`
    for (const part of doc.cookie.split(';')) {
      const trimmed = part.trim()
      if (trimmed.startsWith(prefix)) {
        return trimmed.slice(prefix.length) || null
      }
    }
    return null
  } catch {
    return null
  }
}

/** TTL-enforced read of the localStorage record only — no cookie fallback. */
function readStoredAttribution(storage: Storage): Attribution | null {
  const raw = storage.getItem(STORAGE_KEY)
  if (raw === null) return null
  try {
    const parsed = JSON.parse(raw) as Partial<Attribution>
    if (typeof parsed?.capturedAt !== 'string') {
      storage.removeItem(STORAGE_KEY)
      return null
    }
    const ageMs = Date.now() - Date.parse(parsed.capturedAt)
    if (Number.isNaN(ageMs) || ageMs > TTL_MS) {
      storage.removeItem(STORAGE_KEY)
      return null
    }
    return parsed as Attribution
  } catch {
    storage.removeItem(STORAGE_KEY)
    return null
  }
}

export function getAttribution(): Attribution | null {
  const storage = getStorage()
  const stored = storage ? readStoredAttribution(storage) : null
  const base = stored ?? readCookieFallback()
  const explicitConsent = readExplicitAdvertisingConsent(getDoc()?.cookie ?? '')
  if (!base) {
    return explicitConsent
      ? { capturedAt: new Date().toISOString(), advertisingConsent: explicitConsent }
      : null
  }
  const current = { ...base }
  if (explicitConsent) current.advertisingConsent = explicitConsent
  else delete current.advertisingConsent
  return current
}

function readCookieFallback(): Attribution | null {
  const repCode = readCookieRepCode()
  if (!repCode) return null
  return { repCode, capturedAt: new Date().toISOString() }
}
